import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { LegalDocumentsBloc } from '@kp/legal-document/legal-documents.bloc';
import { FirstVisitCase } from '@kp/shared/first-visit/first-visit-case.enum';
import { ReturnUrlService } from '@kp/shared/services/return-url.service';
import { DataStoreService, TitleService } from '@kservice';
import { LegalDocumentItem, LegalDocumentType, Status, User } from '@ktypes/models';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { skipWhile, switchMap, take } from 'rxjs/operators';

@Component({
  selector: 'kp-privacy-full',
  templateUrl: './privacy-full.component.html',
  styleUrls: ['./privacy-full.component.scss'],
})
export class PrivacyFullComponent implements OnInit, OnDestroy {
  @Input() fromScreen: FirstVisitCase;
  @Input() showAcceptanceControls = true;
  acceptInProgress: boolean;
  introScreen: boolean;
  isEulaOpen = false;
  eulaAccepted = false;
  eulaContent: string = null;
  parsedEulaHtml = '';
  parsedPrivacyHtml = '';
  privacyAccepted = false;
  privacyContent: string = null;

  private _refreshingLegalDocs = new BehaviorSubject<boolean>(null);
  private _subscriptions: Subscription[] = [];
  private _title = 'Privacy/Eula';

  constructor(
    private _authenticationBloc: AuthenticationBloc,
    private _dataStoreService: DataStoreService,
    private _legalDocumentsBloc: LegalDocumentsBloc,
    private _returnUrlService: ReturnUrlService,
    private _router: Router,
    private _titleService: TitleService
  ) {}

  ngOnInit() {
    // Don't set up any of this if embedded in modal
    this._titleService.set(this._title);

    this._subscriptions.push(
      combineLatest([this._legalDocumentsBloc.legalDocumentStorage$, this._refreshingLegalDocs]).subscribe(
        ([legalDocumentStorage, legalDocsRefreshing]) => {
          if (legalDocsRefreshing) {
            // wait to proceed until legal docs aren't refreshing
            return;
          }

          this.privacyContent = legalDocumentStorage?.privacy?.data?.content;
          this.eulaContent = legalDocumentStorage?.eula?.data?.content;

          // Parse content from API to prepare for DOM insertion
          if (this.privacyContent !== '') {
            this.parsedPrivacyHtml = (legalDocumentStorage?.privacy?.data || {}).contentAsParsedHtml;
          }
          if ((this.eulaContent !== '' && this.fromScreen !== FirstVisitCase.onboarding) || !this.privacyContent) {
            this.parsedEulaHtml = (legalDocumentStorage?.eula?.data || {}).contentAsParsedHtml;
          }
        }
      )
    );

    if (this.showAcceptanceControls) {
      // Always refresh at least once
      this._legalDocumentsBloc.getCurrentLegalDocumentStatus();
      this._refreshingLegalDocs.next(true);

      if (!this._authenticationBloc.isLoggedIn() && this._authenticationBloc.storedOnboarding?.groupId) {
        this.introScreen = false;
        void this._legalDocumentsBloc.fetchLegalDocuments(this._authenticationBloc.storedOnboarding?.groupId);
      }

      this._subscriptions.push(
        this._dataStoreService.user$
          .pipe(
            skipWhile((user) => user == null),
            take(1),
            switchMap((user: User) => {
              return combineLatest([
                this._legalDocumentsBloc.currentLegalDocumentStatus$,
                this._refreshingLegalDocs,
                of(user),
              ]);
            })
          )
          .subscribe(([docStatus, legalDocsRefreshing, user]) => {
            const legalDocumentItems: LegalDocumentItem[] = docStatus?.data;

            if (legalDocsRefreshing) {
              if (docStatus != null && docStatus?.status !== Status.starting) {
                this._refreshingLegalDocs.next(false);
              }
              // wait to proceed until legal docs aren't refreshing
              return;
            }
            if (docStatus.status === Status.error) {
              console.warn('privacy full: errored trying to retrieve legal doc status', docStatus);
              return this._router.navigate([
                this.fromScreen === FirstVisitCase.onboarding ? '/welcome/error' : '/error',
              ]);
            } else if (!legalDocumentItems) {
              this._legalDocumentsBloc.getCurrentLegalDocumentStatus();
            } else {
              const nonSignedDocuments = legalDocumentItems.filter((document) => !document.signed);
              const allDocumentsSigned = legalDocumentItems.every((document) => document.signed);
              if (nonSignedDocuments.length > 0 && user != null) {
                const eulaNeedsSigned = nonSignedDocuments.find((doc) => doc.type === LegalDocumentType.EULA);
                const privacyNeedsSigned = nonSignedDocuments.find((doc) => doc.type === LegalDocumentType.PRIVACY);
                if (eulaNeedsSigned && privacyNeedsSigned) {
                  void this._legalDocumentsBloc.fetchLegalDocuments(user.group?.id);
                } else if (eulaNeedsSigned && !privacyNeedsSigned) {
                  void this._legalDocumentsBloc.fetchLegalDocuments(user.group?.id, LegalDocumentType.EULA);
                } else if (privacyNeedsSigned && !eulaNeedsSigned) {
                  void this._legalDocumentsBloc.fetchLegalDocuments(user.group?.id, LegalDocumentType.PRIVACY);
                }

                if (this._authenticationBloc.isLoggedIn()) {
                  this.introScreen = true;
                }
              } else if (allDocumentsSigned && this.fromScreen === FirstVisitCase.onboarding) {
                void this._router.navigate(['/welcome/signup']);
              }
            }
          })
      );

      this._legalDocumentsBloc.resetServerSideLegalDocumentsAcceptance();
      this._subscriptions.push(
        this._legalDocumentsBloc.serverSideAcceptLegalDocumentsResult$.subscribe((result) => {
          if (result === null) {
            return;
          }

          if (!result) {
            if (this.fromScreen === FirstVisitCase.onboarding) {
              void this._router.navigate(['/welcome/error']);
              return;
            }
            void this._router.navigate(['/error']);
            return;
          } else if (result) {
            // once done, clear out
            this._legalDocumentsBloc.clearLegalDocumentStatus();
            this._legalDocumentsBloc.clearLegalDocumentStorage();

            if (this.fromScreen === FirstVisitCase.onboarding) {
              void this._router.navigate(['/welcome/signup']);
              return;
            }
            void this._router.navigate([this._returnUrlService.savedUrl]);
            return;
          }
          this.acceptInProgress = false;
        })
      );
      // If user comes back to this screen after checking the boxes before,
      // ensure they are still checked.
      const previouslyCheckedBoxes = this._legalDocumentsBloc.serverSideAcceptLegalDocumentsResult;
      this.privacyAccepted = previouslyCheckedBoxes;
      this.eulaAccepted = previouslyCheckedBoxes;
    }
  }

  ngOnDestroy() {
    this._subscriptions.forEach((subscribe) => subscribe.unsubscribe());
  }

  continue() {
    if (this.acceptInProgress) {
      return;
    }
    if (this.privacyContent && this.eulaContent && this.privacyAccepted && this.eulaAccepted) {
      this.acceptInProgress = true;
      this._legalDocumentsBloc.serverSideAcceptLegalDocuments();
    }

    if (this.privacyContent && !this.eulaContent && this.privacyAccepted) {
      this.acceptInProgress = true;
      this._legalDocumentsBloc.serverSideAcceptLegalDocuments(LegalDocumentType.PRIVACY);
    }

    if (this.eulaContent && !this.privacyContent && this.eulaAccepted) {
      this.acceptInProgress = true;
      this._legalDocumentsBloc.serverSideAcceptLegalDocuments(LegalDocumentType.EULA);
    }
  }

  openEula(event: MouseEvent) {
    // Added `href=#` for keyboard focus to work. The following 2
    // lines make the link "dead" when clicked.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    // isOpen binding in .html will set this back to false on close
    this.isEulaOpen = true;
  }

  privacyCheckbox(event) {
    this.privacyAccepted = event;
  }
  eulaCheckbox(event) {
    this.eulaAccepted = event;
  }

  get isButtonDisabled() {
    return !(
      (this.privacyContent && this.eulaContent && this.eulaAccepted && this.privacyAccepted) ||
      (this.eulaContent && !this.privacyContent && this.eulaAccepted) ||
      (this.privacyContent && !this.eulaContent && this.privacyAccepted)
    );
  }
}
